/**
 * DatasetRows — the record's "Data" tab (spec §2).
 *
 * One page of `GET /datasets/{id}/rows` in a sortable table, with a debounced search box,
 * prev/next paging and a CSV export of the whole filtered set. The provider decides the
 * columns (`columns[]`, already translated server-side), so nothing here knows what a
 * baseline row or an indicator value looks like — it prints whatever arrived, right-aligning
 * the columns the server called numeric.
 *
 * Only the record draws it, and only when `has_rows` is true and the caller's `access` is not
 * `none` (see `DatasetRecord`), so a 403/404 here is a server-side surprise rather than a
 * state the reader can navigate into — it still says so rather than showing a blank panel.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  ApiError,
  datasetRowsCsvUrl,
  fetchDatasetRows,
  queryKeys,
  type DatasetRowsColumn,
} from '../../lib/api';
import { Icon } from '../../lib/icons';
import { formatCell, pageLabel, sortParam, type SortState } from './rows';

/** §2: 50 rows a page. */
export const ROWS_PAGE_SIZE = 50;

/** §2: the search box waits this long after the last keystroke before it asks the server. */
export const ROWS_SEARCH_DEBOUNCE_MS = 250;

export interface DatasetRowsProps {
  id: string;
}

/** The glyph in a sorted column's header. Unsorted columns carry none. */
function sortGlyph(sort: SortState | null, key: string): string {
  if (sort?.sort !== key) return '';
  return sort.dir === 'asc' ? ' ▲' : ' ▼';
}

export function DatasetRows({ id }: DatasetRowsProps) {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState | null>(null);
  /** What is in the box… */
  const [search, setSearch] = useState('');
  /** …and what has been asked for. The debounce is the only thing between them. */
  const [q, setQ] = useState('');

  // A new search term is a new result set, so it lands on page 1: staying on page 7 of the
  // unsearched table would show the reader an empty page and no reason for it.
  useEffect(() => {
    if (search === q) return;
    const handle = setTimeout(() => {
      setQ(search);
      setPage(1);
    }, ROWS_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search, q]);

  const params = useMemo(
    () => ({
      page,
      pageSize: ROWS_PAGE_SIZE,
      q,
      sort: sort?.sort ?? null,
      dir: sort?.dir ?? ('asc' as const),
    }),
    [page, q, sort],
  );

  const { data, error, isError, isFetching } = useQuery({
    queryKey: queryKeys.datasetRows(id, params),
    queryFn: fetchDatasetRows,
    // Paging and sorting keep the previous page on screen while the next one loads: the
    // table would otherwise collapse to nothing on every click, and the modal would jump.
    placeholderData: (previous) => previous,
  });

  const columns: DatasetRowsColumn[] = data?.columns ?? [];
  const rows = data?.rows ?? [];
  const count = data?.count ?? 0;
  const pages = Math.max(1, Math.ceil(count / ROWS_PAGE_SIZE));
  // `pageLabel` groups in the interface language and hands back strings under `from`/`to`/
  // `total` — `total` rather than `count`, which is i18next's reserved plural selector and
  // would have to stay an ungrouped number.
  const shown = pageLabel(page, ROWS_PAGE_SIZE, count);

  const atStart = page <= 1;
  const atEnd = page >= pages;

  const onSort = (key: string) => {
    setSort(sortParam(sort, key));
    setPage(1);
  };

  return (
    // The catalogue grid's own treatment (`.cat-main.loading`): the page that is already on
    // screen greys out while the next one loads rather than disappearing, and `aria-busy`
    // says so for a reader who cannot see the grey.
    <div
      className={isFetching ? 'rows-panel loading' : 'rows-panel'}
      aria-busy={isFetching || undefined}
    >
      <div className="rows-tools">
        <input
          className="rows-search"
          data-testid="record-rows-search"
          type="search"
          value={search}
          placeholder={t('catalogue.record.rowsSearch')}
          aria-label={t('catalogue.record.rowsSearch')}
          onChange={(e) => setSearch(e.target.value)}
          // Escape clears the search rather than bubbling to the dialog's own Escape
          // handler (`DatasetRecord.tsx`, guarded on this element) and closing the record.
          onKeyDown={(e) => {
            if (e.key === 'Escape' && search !== '') {
              e.stopPropagation();
              setSearch('');
            }
          }}
        />
        <a
          className="btn ghost rows-export"
          data-testid="record-rows-export"
          href={datasetRowsCsvUrl(id, { q, sort: params.sort, dir: params.dir })}
        >
          <Icon name="download" size={14} /> {t('catalogue.record.rowsExport')}
        </a>
      </div>

      {isError && !data ? (
        <p className="rows-note" data-testid="record-rows-error">
          {t(
            error instanceof ApiError && error.status === 403
              ? 'catalogue.record.rowsDenied'
              : 'catalogue.record.rowsError',
          )}
        </p>
      ) : !data ? (
        // First load: there is no table to dim yet, so the panel says what it is doing. The
        // `aria-busy` is on the panel, so a screen reader hears it once rather than per row.
        <p className="rows-note" data-testid="record-rows-loading">
          {t('catalogue.record.rowsLoading')}
        </p>
      ) : rows.length === 0 ? (
        // Two different nothings: a search that matched none of a populated dataset, and a
        // dataset with no rows at all. They read differently and they are found differently,
        // so they carry their own test ids.
        q ? (
          <p className="rows-note" data-testid="record-rows-empty">
            {t('catalogue.record.rowsEmpty')}
          </p>
        ) : (
          <p className="rows-note" data-testid="record-rows-none">
            {t('catalogue.record.rowsNone')}
          </p>
        )
      ) : (
        <div className="rows-wrap">
          <table className="mini rows-table" data-testid="record-rows">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={col.kind === 'number' ? 'num' : undefined}
                    aria-sort={
                      sort?.sort === col.key
                        ? sort.dir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <button
                      type="button"
                      className="rows-sort"
                      title={t('catalogue.record.rowsSort', { column: col.label })}
                      onClick={() => onSort(col.key)}
                    >
                      {col.label}
                      {sortGlyph(sort, col.key)}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                // A provider row carries no id of its own, so the index within the page is
                // the only key there is — and it is a stable one, because a page is replaced
                // wholesale rather than spliced.
                <tr key={i}>
                  {columns.map((col) => (
                    <td key={col.key} className={col.kind === 'number' ? 'num' : undefined}>
                      {formatCell(row[col.key], col.kind, col.key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rows-foot">
        <span className="rows-count" data-testid="record-rows-count">
          {t('catalogue.record.rowsCount', shown)}
        </span>
        <span className="rows-pager">
          {/*
            `aria-disabled`, **not** `disabled`: paging to the last page puts the `disabled`
            attribute on the button the reader has just clicked, and a disabled element cannot
            hold focus — the browser drops it to `<body>`, outside `.modal-back`, where the
            dialog's Tab trap can no longer see it. So the buttons stay focusable and inert,
            the way `SignInGate`'s and `DomainRail`'s inert controls do, and the handler
            returns early rather than relying on the attribute to stop the click.
          */}
          <button
            type="button"
            className={atStart ? 'btn ghost off' : 'btn ghost'}
            data-testid="record-rows-prev"
            aria-disabled={atStart || undefined}
            onClick={() => {
              if (atStart) return;
              setPage((p) => Math.max(1, p - 1));
            }}
          >
            {t('catalogue.record.prev')}
          </button>
          <button
            type="button"
            className={atEnd ? 'btn ghost off' : 'btn ghost'}
            data-testid="record-rows-next"
            aria-disabled={atEnd || undefined}
            onClick={() => {
              if (atEnd) return;
              setPage((p) => Math.min(pages, p + 1));
            }}
          >
            {t('catalogue.record.next')}
          </button>
        </span>
      </div>
    </div>
  );
}

export default DatasetRows;
